import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, hasAnyRole } from "@/context/AuthContext";
import { DeleteConfirmProvider } from "@/components/DeleteConfirm";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Parties = lazy(() => import("@/pages/Parties"));
const Items = lazy(() => import("@/pages/Items"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Production = lazy(() => import("@/pages/Production"));
const Reports = lazy(() => import("@/pages/Reports"));
const Users = lazy(() => import("@/pages/Users"));
const Machines = lazy(() => import("@/pages/Machines"));
const PaperPrices = lazy(() => import("@/pages/PaperPrices"));
const JobPrint = lazy(() => import("@/pages/JobPrint"));
const VehicleTypes = lazy(() => import("@/pages/VehicleTypes"));
const Vehicles = lazy(() => import("@/pages/Vehicles"));
const Drivers = lazy(() => import("@/pages/Drivers"));
const DispatchSlips = lazy(() => import("@/pages/DispatchSlips"));
const SlipPrint = lazy(() => import("@/pages/SlipPrint"));
const Company = lazy(() => import("@/pages/Company"));
const Transport = lazy(() => import("@/pages/Transport"));
const AnalyticsHub = lazy(() => import("@/pages/AnalyticsHub"));
const MarginReport = lazy(() => import("@/pages/MarginReport"));
const WastageReport = lazy(() => import("@/pages/WastageReport"));
const StageWeightReport = lazy(() => import("@/pages/StageWeightReport"));
const PartyProductionReport = lazy(() => import("@/pages/PartyProductionReport"));
const PartyDispatchReport = lazy(() => import("@/pages/PartyDispatchReport"));
const ConversionBillingReport = lazy(() => import("@/pages/ConversionBillingReport"));
const SpeedReport = lazy(() => import("@/pages/SpeedReport"));
const AlertsReport = lazy(() => import("@/pages/AlertsReport"));
const FreightByVehicle = lazy(() => import("@/pages/FreightByVehicle"));
const DailyProductionSummary = lazy(() => import("@/pages/DailyProductionSummary"));
const WastageConsumables = lazy(() => import("@/pages/WastageConsumables"));
const WastageConsumablesMonthly = lazy(() => import("@/pages/WastageConsumablesMonthly"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const RndSpecs = lazy(() => import("@/pages/RndSpecs"));
const PLPerKgReport = lazy(() => import("@/pages/PLPerKgReport"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const PaperOrders = lazy(() => import("@/pages/PaperOrders"));
const PaperOrderDetail = lazy(() => import("@/pages/PaperOrderDetail"));
const POPendingReport = lazy(() => import("@/pages/POPendingReport"));
const BFMonthlyReport = lazy(() => import("@/pages/BFMonthlyReport"));
const PaperOrderPrint = lazy(() => import("@/pages/PaperOrderPrint"));
const PaperOrderSupplierPrint = lazy(() => import("@/pages/PaperOrderSupplierPrint"));
const JobTracking = lazy(() => import("@/pages/JobTracking"));
const SOTracking = lazy(() => import("@/pages/SOTracking"));
const DeliverySchedule = lazy(() => import("@/pages/DeliverySchedule"));
const GRN = lazy(() => import("@/pages/GRN"));
const Consumption = lazy(() => import("@/pages/Consumption"));
const Stock = lazy(() => import("@/pages/Stock"));
const OtherPurchases = lazy(() => import("@/pages/OtherPurchases"));
const OtherItems = lazy(() => import("@/pages/OtherItems"));
const DispatchDetail = lazy(() => import("@/pages/DispatchDetail"));
const ItemDetail = lazy(() => import("@/pages/ItemDetail"));
const MaterialYieldReport = lazy(() => import("@/pages/MaterialYieldReport"));
const ReorderReport = lazy(() => import("@/pages/ReorderReport"));
const StageReadyReport = lazy(() => import("@/pages/StageReadyReport"));
const SalesOrders = lazy(() => import("@/pages/SalesOrders"));
const SalesOrderReports = lazy(() => import("@/pages/SalesOrderReports"));
const StockRegister = lazy(() => import("@/pages/StockRegister"));
const PurchaseRegister = lazy(() => import("@/pages/PurchaseRegister"));
const SalesRegister = lazy(() => import("@/pages/SalesRegister"));
const SalesAnalytics = lazy(() => import("@/pages/SalesAnalytics"));
const Converters = lazy(() => import("@/pages/Converters"));
const ConverterChallans = lazy(() => import("@/pages/ConverterChallans"));
const ConverterChallanDetail = lazy(() => import("@/pages/ConverterChallanDetail"));
const OutsourcedConversionReport = lazy(() => import("@/pages/OutsourcedConversionReport"));
const PaperPlanning = lazy(() => import("@/pages/PaperPlanning"));
const PaperOverspecReport = lazy(() => import("@/pages/PaperOverspecReport"));
const SpareRequests = lazy(() => import("@/pages/SpareRequests"));
const SpareMasters = lazy(() => import("@/pages/SpareMasters"));
const SpareReports = lazy(() => import("@/pages/SpareReports"));
const Reconcile = lazy(() => import("@/pages/Reconcile"));
const CoaRegister = lazy(() => import("@/pages/CoaRegister"));
const CoaPrint = lazy(() => import("@/pages/CoaPrint"));

// Default landing route per role
const ROLE_HOME = {
  super_admin: "/",
  manager: "/",
  prod_entry: "/production",
  job_entry: "/jobs",
  dispatch: "/dispatch-slips",
  grn_entry: "/grn",
  paper_entry: "/consumption",
  viewer: "/stock",
  stock_view: "/stock",
  sales: "/sales-orders",
  spares_request: "/spare-requests",
  accounts: "/spare-requests",
};

function PageFallback() {
  return (
    <div className="p-8 space-y-4 animate-pulse" data-testid="page-loading">
      <div className="h-7 w-56 rounded bg-zinc-200" />
      <div className="h-4 w-80 rounded bg-zinc-100" />
      <div className="h-64 rounded bg-zinc-100 mt-6" />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-8 text-[var(--text-3)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !hasAnyRole(user, roles)) {
    return <Navigate to={ROLE_HOME[user.role] || "/login"} replace />;
  }
  return children;
}

const MGR = ["manager", "super_admin"];
const CHALLAN_ROLES = ["manager", "super_admin", "paper_entry", "grn_entry", "prod_entry", "stock_view"];
const MGR_PROD = ["manager", "super_admin", "prod_entry"];
const MGR_JOB = ["manager", "super_admin", "job_entry"];
const MGR_DISP = ["manager", "super_admin", "dispatch"];
const JOB_VIEW = ["manager", "super_admin", "job_entry", "dispatch"];
const MGR_GRN = ["manager", "super_admin", "grn_entry"];
const MGR_CONS = ["manager", "super_admin", "paper_entry"];
const JOBS_LIST = ["manager", "super_admin", "job_entry", "dispatch", "viewer"];
const MGR_SALES = ["manager", "super_admin", "sales"];
const SPARES_ROLES = ["manager", "super_admin", "spares_request", "grn_entry", "accounts"];
const ALL_ROLES = ["manager", "super_admin", "prod_entry", "job_entry", "dispatch", "grn_entry", "paper_entry", "viewer", "sales", "spares_request", "accounts"];
const STOCK_PAGE_ROLES = [...ALL_ROLES, "stock_view"];
const COA_ROLES = ["manager", "super_admin", "dispatch", "accounts"];

function App() {
  useEffect(() => {
    const kill = () => {
      document.querySelectorAll(
        'a#emergent-badge, [id^="emergent-"], a[href*="emergent.sh"][target="_blank"]'
      ).forEach((n) => {
        try { n.remove(); } catch { n.style.setProperty("display", "none", "important"); }
      });
    };
    kill();
    const obs = new MutationObserver(kill);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return (
    <AuthProvider>
      <DeleteConfirmProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/jobs/:id/print" element={<Protected roles={JOB_VIEW}><JobPrint /></Protected>} />
          <Route path="/slips/:id/print" element={<Protected roles={MGR_DISP}><SlipPrint /></Protected>} />
          <Route path="/paper-orders/:id/print" element={<Protected roles={MGR}><PaperOrderPrint /></Protected>} />
          <Route path="/paper-orders/:id/supplier-print" element={<Protected roles={MGR}><PaperOrderSupplierPrint /></Protected>} />
          <Route path="/coa/slip/:slipId/print" element={<Protected roles={COA_ROLES}><CoaPrint mode="slip" /></Protected>} />
          <Route path="/coa/:id/print" element={<Protected roles={COA_ROLES}><CoaPrint mode="single" /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Protected roles={ALL_ROLES}><Dashboard /></Protected>} />
            <Route path="/production" element={<Navigate to="/production/board" replace />} />
            <Route path="/production/:stage" element={<Protected roles={MGR_PROD}><Production /></Protected>} />
            <Route path="/jobs" element={<Protected roles={JOBS_LIST}><Jobs /></Protected>} />
            <Route path="/sales-orders" element={<Protected roles={MGR_SALES}><SalesOrders /></Protected>} />
            <Route path="/sales-orders/:id/tracking" element={<Protected roles={["manager", "super_admin", "sales", "job_entry"]}><SOTracking /></Protected>} />
            <Route path="/delivery-schedule" element={<Protected roles={ALL_ROLES}><DeliverySchedule /></Protected>} />
            <Route path="/reports/sales-orders" element={<Protected roles={MGR}><SalesOrderReports /></Protected>} />
            <Route path="/assistant" element={<Protected roles={["super_admin"]}><Assistant /></Protected>} />
            <Route path="/rnd" element={<Protected roles={MGR_SALES}><RndSpecs /></Protected>} />
            <Route path="/spare-requests" element={<Protected roles={SPARES_ROLES}><SpareRequests /></Protected>} />
            <Route path="/spare-masters" element={<Protected roles={MGR}><SpareMasters /></Protected>} />
            <Route path="/reports/spares" element={<Protected roles={MGR}><SpareReports /></Protected>} />
            <Route path="/jobs/:id/tracking" element={<Protected roles={["manager", "super_admin", "job_entry", "dispatch", "prod_entry"]}><JobTracking /></Protected>} />
            <Route path="/items" element={<Protected roles={MGR}><Items /></Protected>} />
            <Route path="/converters" element={<Protected roles={MGR}><Converters /></Protected>} />
            <Route path="/converter-challans" element={<Protected roles={CHALLAN_ROLES}><ConverterChallans /></Protected>} />
            <Route path="/converter-challans/:id" element={<Protected roles={CHALLAN_ROLES}><ConverterChallanDetail /></Protected>} />
            <Route path="/reports/outsourced-conversion" element={<Protected roles={MGR}><OutsourcedConversionReport /></Protected>} />
            <Route path="/items/:id" element={<Protected roles={MGR}><ItemDetail /></Protected>} />
            <Route path="/parties" element={<Protected roles={MGR}><Parties /></Protected>} />
            <Route path="/machines" element={<Protected roles={MGR}><Machines /></Protected>} />
            <Route path="/paper-prices" element={<Protected roles={MGR}><PaperPrices /></Protected>} />
            <Route path="/vehicle-types" element={<Protected roles={MGR}><VehicleTypes /></Protected>} />
            <Route path="/vehicles" element={<Protected roles={MGR}><Vehicles /></Protected>} />
            <Route path="/drivers" element={<Protected roles={MGR}><Drivers /></Protected>} />
            <Route path="/transport" element={<Protected roles={MGR}><Transport /></Protected>} />
            <Route path="/suppliers" element={<Protected roles={MGR}><Suppliers /></Protected>} />
            <Route path="/paper-orders" element={<Protected roles={MGR}><PaperOrders /></Protected>} />
            <Route path="/paper-orders/:id" element={<Protected roles={MGR}><PaperOrderDetail /></Protected>} />
            <Route path="/other-purchases" element={<Protected roles={MGR}><OtherPurchases /></Protected>} />
            <Route path="/other-items" element={<Protected roles={MGR}><OtherItems /></Protected>} />
            <Route path="/grn" element={<Protected roles={MGR_GRN}><GRN /></Protected>} />
            <Route path="/consumption" element={<Protected roles={MGR_CONS}><Consumption /></Protected>} />
            <Route path="/stock" element={<Protected roles={STOCK_PAGE_ROLES}><Stock /></Protected>} />
            <Route path="/stock/reconcile" element={<Protected roles={MGR}><Reconcile /></Protected>} />
            <Route path="/reports/po-pending" element={<Protected roles={MGR}><POPendingReport /></Protected>} />
            <Route path="/reports/bf-monthly" element={<Protected roles={MGR}><BFMonthlyReport /></Protected>} />
            <Route path="/reports/reorder-list" element={<Protected roles={MGR_GRN}><ReorderReport /></Protected>} />
            <Route path="/reports/stage-ready" element={<Protected roles={ALL_ROLES}><StageReadyReport /></Protected>} />
            <Route path="/dispatch-slips" element={<Protected roles={MGR_DISP}><DispatchSlips /></Protected>} />
            <Route path="/dispatch-slips/:id" element={<Protected roles={MGR_DISP}><DispatchDetail /></Protected>} />
            <Route path="/wastage-consumables" element={<Protected roles={MGR_PROD}><WastageConsumables /></Protected>} />
            <Route path="/company" element={<Protected roles={MGR}><Company /></Protected>} />
            <Route path="/reports/analytics" element={<Protected roles={MGR}><AnalyticsHub /></Protected>} />
            <Route path="/reports/pl-per-kg" element={<Protected roles={MGR}><PLPerKgReport /></Protected>} />
            <Route path="/reports/margin" element={<Protected roles={MGR}><MarginReport /></Protected>} />
            <Route path="/reports/wastage" element={<Protected roles={MGR}><WastageReport /></Protected>} />
            <Route path="/reports/stage-weight" element={<Protected roles={MGR}><StageWeightReport /></Protected>} />
            <Route path="/reports/party-production" element={<Protected roles={MGR}><PartyProductionReport /></Protected>} />
            <Route path="/reports/party-dispatch" element={<Protected roles={MGR}><PartyDispatchReport /></Protected>} />
            <Route path="/reports/speed" element={<Protected roles={MGR}><SpeedReport /></Protected>} />
            <Route path="/reports/alerts" element={<Protected roles={MGR}><AlertsReport /></Protected>} />
            <Route path="/reports/freight-by-vehicle" element={<Protected roles={MGR}><FreightByVehicle /></Protected>} />
            <Route path="/reports/daily-summary" element={<Protected roles={ALL_ROLES}><DailyProductionSummary /></Protected>} />
            <Route path="/reports/wastage-consumables" element={<Protected roles={MGR}><WastageConsumablesMonthly /></Protected>} />
            <Route path="/reports/material-yield" element={<Protected roles={MGR}><MaterialYieldReport /></Protected>} />
            <Route path="/reports/conversion-billing" element={<Protected roles={MGR}><ConversionBillingReport /></Protected>} />
            <Route path="/reports/stock-register" element={<Protected roles={["manager", "super_admin", "stock_view", "paper_entry"]}><StockRegister /></Protected>} />
            <Route path="/reports/purchase-register" element={<Protected roles={MGR}><PurchaseRegister /></Protected>} />
            <Route path="/reports/sales-register" element={<Protected roles={MGR}><SalesRegister /></Protected>} />
            <Route path="/reports/sales-analytics" element={<Protected roles={MGR_SALES}><SalesAnalytics /></Protected>} />
            <Route path="/settings/sales-targets" element={<Navigate to="/reports/sales-analytics?tab=targets" replace />} />
            <Route path="/reports/paper-planning" element={<Protected roles={MGR}><PaperPlanning /></Protected>} />
            <Route path="/reports/paper-overspec" element={<Protected roles={MGR}><PaperOverspecReport /></Protected>} />
            <Route path="/reports/coa" element={<Protected roles={COA_ROLES}><CoaRegister /></Protected>} />
            <Route path="/reports" element={<Protected roles={MGR}><Reports /></Protected>} />
            <Route path="/users" element={<Protected roles={MGR}><Users /></Protected>} />
            <Route path="/activity-log" element={<Protected roles={MGR}><ActivityLog /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </DeleteConfirmProvider>
    </AuthProvider>
  );
}

export default App;
